// Curated preset badges + admin-defined custom badges for festival films.

import type { CSSProperties } from "react";

export const PRESET_BADGES = ["Première", "Voorpremière", "Ladies Night", "Special"] as const;
export type PresetBadge = (typeof PRESET_BADGES)[number];

const PRESET_LOWER = PRESET_BADGES.map((b) => b.toLowerCase());

export function isPresetBadge(label: string): boolean {
  return PRESET_LOWER.includes(label.trim().toLowerCase());
}

export function canonicalPreset(label: string): PresetBadge | null {
  const idx = PRESET_LOWER.indexOf(label.trim().toLowerCase());
  return idx >= 0 ? PRESET_BADGES[idx] : null;
}

/** Tailwind classes for a preset badge chip — bold, glowy, unmistakable. */
export function getBadgeStyle(label: string): string {
  const preset = canonicalPreset(label);
  switch (preset) {
    case "Première":
      return "bg-gold text-gold-foreground border border-gold shadow-[0_0_20px_rgba(212,175,55,0.55)]";
    case "Voorpremière":
      return "bg-gold/15 text-gold border border-gold/70 shadow-[0_0_10px_rgba(212,175,55,0.3)]";
    case "Ladies Night":
      return "bg-gradient-to-r from-pink-500 to-rose-500 text-white border border-pink-200/60 shadow-[0_0_20px_rgba(244,63,94,0.55)]";
    case "Special":
      return "bg-foreground/10 text-foreground border border-foreground/40";
    default:
      return "bg-foreground/10 text-foreground/90 border border-foreground/30";
  }
}

/** Admin-defined badge with free-form colours. */
export type CustomBadge = {
  label: string;
  bg: string;
  fg: string;
  border: string;
  glow?: boolean;
};

function eq(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function findCustomBadge(label: string, customs?: readonly CustomBadge[]): CustomBadge | null {
  if (!customs) return null;
  return customs.find((c) => eq(c.label, label)) ?? null;
}

/** Resolve final className + inline style for any badge label. */
export function resolveBadgeStyle(
  label: string,
  customs?: readonly CustomBadge[],
): { className: string; style?: CSSProperties } {
  if (canonicalPreset(label)) return { className: getBadgeStyle(label) };
  const custom = findCustomBadge(label, customs);
  if (custom) {
    const style: CSSProperties = {
      backgroundColor: custom.bg,
      color: custom.fg,
      borderColor: custom.border,
    };
    if (custom.glow) {
      style.boxShadow = `0 0 18px ${custom.bg}`;
    }
    return { className: "border", style };
  }
  return { className: getBadgeStyle(label) };
}

/**
 * Pick a single "primary" badge for compact surfaces (e.g. homepage poster).
 * Preset order first, then any custom badge, then the first free-form label.
 */
export function pickPrimaryBadge(
  badges: readonly string[],
  customs?: readonly CustomBadge[],
): string | null {
  for (const preset of PRESET_BADGES) {
    const hit = badges.find((b) => canonicalPreset(b) === preset);
    if (hit) return preset;
  }
  if (customs && customs.length > 0) {
    const hit = badges.find((b) => findCustomBadge(b, customs));
    if (hit) return hit;
  }
  return badges[0] ?? null;
}